import { useMemo, useState } from 'react'
import { Power } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { cn } from '@/lib/utils'
import { FEATURE_KEYS, featureKeyLabel } from '@riverpark/utils/feature-access'
import { GATEABLE_ROLES, type FeatureKey, type GateableRole } from '@riverpark/types'
import {
  useMemberFeatureOverrides,
  useRoleFeatureAccess,
  useUpdateMemberFeatureOverride,
  useUpdateRoleFeatureAccess,
} from '@/hooks/use-access-control'
import { useMembersList } from '@/hooks/use-members'

const ROLE_LABELS: Record<GateableRole, string> = {
  member: 'Member',
  committee: 'Committee',
  estate_manager: 'Estate manager',
}

function RoleFeatureCell({
  enabled,
  disabled,
  onToggle,
}: {
  enabled: boolean
  disabled: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      title={enabled ? 'Turn off' : 'Turn on'}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-sans text-xs font-semibold uppercase tracking-wide transition-opacity disabled:cursor-not-allowed disabled:opacity-50',
        enabled
          ? 'bg-status-paid-bg text-status-paid-text hover:opacity-80'
          : 'bg-status-draft-bg text-status-draft-text hover:opacity-80',
      )}
    >
      <Power size={12} strokeWidth={2} />
      {enabled ? 'On' : 'Off'}
    </button>
  )
}

function RoleMatrix() {
  const { data, isLoading } = useRoleFeatureAccess()
  const update = useUpdateRoleFeatureAccess()

  const enabledByCell = useMemo(() => {
    const map = new Map<string, boolean>()
    for (const row of data?.data ?? []) {
      map.set(`${row.role}:${row.featureKey}`, row.enabled)
    }
    return map
  }, [data])

  async function handleToggle(role: GateableRole, featureKey: FeatureKey, current: boolean) {
    await update.mutateAsync({ role, featureKey, enabled: !current })
  }

  return (
    <div className="overflow-x-auto rounded border border-border-2 bg-card">
      <table className="w-full min-w-[520px] border-collapse font-sans text-sm">
        <thead>
          <tr className="border-b border-border-2 bg-cream-2/50">
            <th className="px-5 py-3 text-left font-medium text-muted-foreground">Feature</th>
            {GATEABLE_ROLES.map((role) => (
              <th key={role} className="px-5 py-3 text-left font-medium text-muted-foreground">
                {ROLE_LABELS[role]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {FEATURE_KEYS.map((featureKey) => (
            <tr key={featureKey} className="border-b border-border-2 last:border-b-0">
              <td className="px-5 py-3 font-medium text-ink">{featureKeyLabel(featureKey)}</td>
              {GATEABLE_ROLES.map((role) => {
                const enabled = enabledByCell.get(`${role}:${featureKey}`) ?? true
                return (
                  <td key={role} className="px-5 py-3">
                    <RoleFeatureCell
                      enabled={enabled}
                      disabled={isLoading || update.isPending}
                      onToggle={() => void handleToggle(role, featureKey, enabled)}
                    />
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

type OverrideState = 'inherit' | 'on' | 'off'

function MemberOverridePanel() {
  const { data: committeeData } = useMembersList({ role: 'committee', limit: 50 })
  const { data: estateManagerData } = useMembersList({ role: 'estate_manager', limit: 50 })
  const { data: roleAccessData } = useRoleFeatureAccess()
  const { data: overridesData } = useMemberFeatureOverrides()
  const update = useUpdateMemberFeatureOverride()

  const candidates = useMemo(
    () => [...(committeeData?.data ?? []), ...(estateManagerData?.data ?? [])],
    [committeeData, estateManagerData],
  )
  const [selectedId, setSelectedId] = useState<string>('')
  const selected = candidates.find((m) => m.id === selectedId)

  const roleDefaultByFeature = useMemo(() => {
    const map = new Map<FeatureKey, boolean>()
    if (!selected) return map
    for (const row of roleAccessData?.data ?? []) {
      if (row.role === selected.role) map.set(row.featureKey, row.enabled)
    }
    return map
  }, [roleAccessData, selected])

  const overrideByFeature = useMemo(() => {
    const map = new Map<FeatureKey, boolean>()
    for (const row of overridesData?.data ?? []) {
      if (row.memberId === selectedId) map.set(row.featureKey, row.enabled)
    }
    return map
  }, [overridesData, selectedId])

  async function handleSetState(featureKey: FeatureKey, state: OverrideState) {
    if (!selectedId) return
    const enabled = state === 'inherit' ? null : state === 'on'
    await update.mutateAsync({ memberId: selectedId, featureKey, enabled })
  }

  return (
    <div className="space-y-4">
      <div className="max-w-sm space-y-1.5">
        <label className="font-sans text-sm font-medium text-ink" htmlFor="override-member">
          Committee / Estate manager account
        </label>
        <select
          id="override-member"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          className="flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
        >
          <option value="">Select an account…</option>
          {candidates.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name} ({ROLE_LABELS[m.role as GateableRole]})
            </option>
          ))}
        </select>
      </div>

      {selected && (
        <div className="overflow-x-auto rounded border border-border-2 bg-card">
          <table className="w-full min-w-[520px] border-collapse font-sans text-sm">
            <thead>
              <tr className="border-b border-border-2 bg-cream-2/50">
                <th className="px-5 py-3 text-left font-medium text-muted-foreground">Feature</th>
                <th className="px-5 py-3 text-left font-medium text-muted-foreground">Access</th>
              </tr>
            </thead>
            <tbody>
              {FEATURE_KEYS.map((featureKey) => {
                const override = overrideByFeature.get(featureKey)
                const roleDefault = roleDefaultByFeature.get(featureKey) ?? true
                const state: OverrideState =
                  override === undefined ? 'inherit' : override ? 'on' : 'off'
                return (
                  <tr key={featureKey} className="border-b border-border-2 last:border-b-0">
                    <td className="px-5 py-3 font-medium text-ink">
                      {featureKeyLabel(featureKey)}
                    </td>
                    <td className="px-5 py-3">
                      <div className="inline-flex overflow-hidden rounded border border-border-2">
                        {(['inherit', 'on', 'off'] as const).map((option) => (
                          <button
                            key={option}
                            type="button"
                            disabled={update.isPending}
                            onClick={() => void handleSetState(featureKey, option)}
                            className={cn(
                              'px-3 py-1.5 font-sans text-xs font-medium capitalize transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                              state === option
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-card text-muted-foreground hover:bg-cream-2',
                            )}
                          >
                            {option === 'inherit'
                              ? `Inherit (${roleDefault ? 'On' : 'Off'})`
                              : option}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export function AdminAccessControl() {
  return (
    <PageShell>
      <PageHeader
        label="System"
        title="Access control"
        description="Turn a whole section on or off for a role, or override it for one specific Committee/Estate manager account. Admin always has full access."
      />

      <div className="space-y-3">
        <h2 className="font-serif text-lg text-ink">Role defaults</h2>
        <RoleMatrix />
      </div>

      <div className="space-y-3">
        <h2 className="font-serif text-lg text-ink">Per-account overrides</h2>
        <MemberOverridePanel />
      </div>
    </PageShell>
  )
}
